import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Calendar, Image, Check, Clock, Eye, EyeOff, ArrowLeft, Users, FileText, Video, Link as LinkIcon, Download, Send, ChevronDown, ChevronUp, Upload, X, ExternalLink, DollarSign, Unlock, Crown, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminChallenges = () => {
  const [challenges, setChallenges] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expandedChallenge, setExpandedChallenge] = useState(null);
  const [registrations, setRegistrations] = useState({});
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    start_date: '',
    end_date: '',
    max_participants: '',
    discount_code: '',
    discount_percentage: '',
    image_url: '',
    status: 'active',
    telegram_link: '',
    whatsapp_link: '',
    community_type: 'telegram', // telegram o whatsapp
    materials: [], // Array de materiales: { name, type, url }
    access_type: 'paid', // free, paid, levels
    access_levels: [], // ["nivel_1", "nivel_2", "premium"]
    nutrition_goal: '', // ganar, perder, mantener - objetivo nutricional del challenge
    has_nutrition_plan: false, // si el challenge incluye plan nutricional
    original_price: '' // precio gancho (tachado)
  });

  // Estados para subir materiales
  const [materialFile, setMaterialFile] = useState(null);
  const [materialName, setMaterialName] = useState('');
  const [materialUrl, setMaterialUrl] = useState('');
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const materialInputRef = useRef(null);
  
  // Estados para crear cuentas de prueba
  const [showTestAccountModal, setShowTestAccountModal] = useState(null); // challenge_id
  const [testAccountData, setTestAccountData] = useState({ name: '', email: '', password: '' });
  const [creatingTestAccount, setCreatingTestAccount] = useState(false);

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${BACKEND_URL}/api/challenges`);
      setChallenges(response.data);
      
      // Obtener inscripciones de cada challenge
      for (const challenge of response.data) {
        try {
          const regResponse = await axios.get(
            `${BACKEND_URL}/api/admin/challenge-registrations/${challenge.challenge_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setRegistrations(prev => ({
            ...prev,
            [challenge.challenge_id]: regResponse.data
          }));
        } catch (err) {
          console.log('No registrations for', challenge.challenge_id);
        }
      }
    } catch (error) {
      toast.error('Error al cargar challenges');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('session_token');
    setUploading(true);

    try {
      let imageUrl = formData.image_url;

      // Si hay una imagen nueva, subirla primero
      if (imageFile) {
        const imgFormData = new FormData();
        imgFormData.append('file', imageFile);
        imgFormData.append('section', 'challenge');
        
        const uploadResponse = await axios.post(
          `${BACKEND_URL}/api/admin/site-config/upload-image`,
          imgFormData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
            } 
          }
        );
        imageUrl = uploadResponse.data.url;
      }

      const challengeData = { ...formData, image_url: imageUrl };
      console.log('Enviando challengeData:', challengeData); // Debug

      if (editingChallenge) {
        const challengeId = editingChallenge.challenge_id || editingChallenge.content_id;
        console.log('Actualizando challenge con ID:', challengeId); // Debug
        await axios.put(
          `${BACKEND_URL}/api/admin/challenges/${challengeId}`,
          challengeData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Challenge actualizado');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/challenges`,
          challengeData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Challenge creado');
      }
      setIsOpen(false);
      resetForm();
      fetchChallenges();
    } catch (error) {
      console.error('Error saving challenge:', error);
      let errorMessage = 'Error desconocido';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(`Error al guardar challenge: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (challengeId) => {
    if (!window.confirm('¿Estás segura de eliminar este challenge?')) return;

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/challenges/${challengeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Challenge eliminado');
      fetchChallenges();
    } catch (error) {
      toast.error('Error al eliminar challenge');
    }
  };

  const handleEdit = (challenge) => {
    console.log('Editando challenge:', challenge); // Debug
    setEditingChallenge(challenge);
    setFormData({
      title: challenge.title || challenge.name || '',
      description: challenge.description || '',
      price: challenge.price || '',
      start_date: challenge.start_date?.split('T')[0] || '',
      end_date: challenge.end_date?.split('T')[0] || '',
      max_participants: challenge.max_participants || '',
      discount_code: challenge.discount_code || '',
      discount_percentage: challenge.discount_percentage || '',
      image_url: challenge.image_url || challenge.file_path || '',
      status: challenge.status || 'active',
      telegram_link: challenge.telegram_link || '',
      whatsapp_link: challenge.whatsapp_link || '',
      community_type: challenge.community_type || 'telegram',
      materials: challenge.materials || challenge.challenge_materials || [],
      access_type: challenge.access_type || 'paid',
      access_levels: challenge.access_levels || [],
      nutrition_goal: challenge.nutrition_goal || '',
      has_nutrition_plan: challenge.has_nutrition_plan || false
    });
    setIsOpen(true);
  };

  const resetForm = () => {
    setEditingChallenge(null);
    setImageFile(null);
    setMaterialFile(null);
    setMaterialName('');
    setFormData({
      title: '',
      description: '',
      price: '',
      start_date: '',
      end_date: '',
      max_participants: '',
      discount_code: '',
      discount_percentage: '',
      image_url: '',
      status: 'active',
      telegram_link: '',
      whatsapp_link: '',
      community_type: 'telegram',
      materials: [],
      access_type: 'paid',
      access_levels: [],
      nutrition_goal: '',
      has_nutrition_plan: false
    });
  };

  const toggleAccessLevel = (level) => {
    setFormData(prev => {
      const currentLevels = prev.access_levels || [];
      if (currentLevels.includes(level)) {
        return { ...prev, access_levels: currentLevels.filter(l => l !== level) };
      } else {
        return { ...prev, access_levels: [...currentLevels, level] };
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Subir material
  const handleUploadMaterial = async () => {
    if (!materialFile || !materialName.trim()) {
      toast.error('Debes seleccionar un archivo y darle un nombre');
      return;
    }

    const token = localStorage.getItem('session_token');
    setUploadingMaterial(true);

    try {
      // Subir archivo al servidor
      const formDataUpload = new FormData();
      formDataUpload.append('file', materialFile);
      formDataUpload.append('section', 'challenge_material');

      const uploadRes = await axios.post(
        `${BACKEND_URL}/api/admin/upload-file`,
        formDataUpload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fileType = materialFile.type.includes('pdf') ? 'pdf' : 
                       materialFile.type.includes('video') ? 'video' : 'file';

      const newMaterial = {
        name: materialName.trim(),
        type: fileType,
        url: uploadRes.data.url,
        size: materialFile.size
      };

      // Agregar al estado local - se guardará con "Actualizar"
      setFormData(prev => ({
        ...prev,
        materials: [...prev.materials, newMaterial]
      }));
      
      toast.success('Material agregado. Haz clic en "Actualizar" para guardar.');
      setMaterialFile(null);
      setMaterialName('');
    } catch (error) {
      console.error('Error uploading material:', error);
      const errorMsg = error.response?.data?.detail || 'Error al subir el material';
      toast.error(errorMsg);
    } finally {
      setUploadingMaterial(false);
    }
  };

  // Agregar material por URL (Google Drive, Dropbox, etc.)
  const handleAddMaterialByUrl = async () => {
    if (!materialUrl || !materialName.trim()) {
      toast.error('Debes ingresar nombre y URL del material');
      return;
    }
    
    // Determinar tipo de archivo por la URL
    let fileType = 'pdf';
    if (materialUrl.toLowerCase().includes('.mp4') || materialUrl.toLowerCase().includes('.mov') || materialUrl.toLowerCase().includes('video')) {
      fileType = 'video';
    }

    const newMaterial = {
      name: materialName.trim(),
      type: fileType,
      url: materialUrl.trim(),
      size: 0
    };

    // Simplemente agregar al estado local - se guardará con "Actualizar"
    setFormData(prev => ({
      ...prev,
      materials: [...prev.materials, newMaterial]
    }));
    
    toast.success('Material agregado. Haz clic en "Actualizar" para guardar.');
    setMaterialUrl('');
    setMaterialName('');
  };

  // Eliminar material
  const removeMaterial = (index) => {
    setFormData(prev => ({
      ...prev,
      materials: prev.materials.filter((_, i) => i !== index)
    }));
    toast.success('Material eliminado. Haz clic en "Actualizar" para guardar.');
  };

  // Exportar inscritos a CSV
  const exportRegistrationsToCSV = (challengeId, challengeTitle) => {
    const regs = registrations[challengeId] || [];
    if (regs.length === 0) {
      toast.error('No hay inscritos para exportar');
      return;
    }

    const headers = ['Nombre', 'Email', 'WhatsApp', 'Fecha Inscripción'];
    const rows = regs.map(r => [
      r.name || 'N/A',
      r.email || 'N/A',
      r.whatsapp || 'N/A',
      r.registered_at ? new Date(r.registered_at).toLocaleDateString('es-ES') : 'N/A'
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inscritos_${challengeTitle.replace(/\s+/g, '_')}.csv`;
    link.click();
    toast.success('CSV exportado');
  };

  // Crear cuenta de prueba para un challenge
  const handleCreateTestAccount = async (challengeId) => {
    if (!testAccountData.email || !testAccountData.password) {
      toast.error('Email y contraseña son requeridos');
      return;
    }
    
    if (testAccountData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setCreatingTestAccount(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/challenges/${challengeId}/create-test-account`,
        {
          name: testAccountData.name,
          email: testAccountData.email.toLowerCase(),
          password: testAccountData.password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Cuenta creada: ${testAccountData.email}`);
      setShowTestAccountModal(null);
      setTestAccountData({ name: '', email: '', password: '' });
      
      // Recargar challenges (incluye inscripciones)
      fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cuenta');
    } finally {
      setCreatingTestAccount(false);
    }
  };

  // Eliminar inscripción de un challenge
  const handleDeleteRegistration = async (challengeId, registrationId, email) => {
    if (!window.confirm(`¿Eliminar la inscripción de ${email}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(
        `${BACKEND_URL}/api/admin/challenges/${challengeId}/registrations/${registrationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Inscripción de ${email} eliminada`);
      // Recargar challenges (incluye inscripciones)
      fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar inscripción');
    }
  };

  // Separar challenges activos y próximamente
  const activeChallenges = challenges.filter(c => c.status !== 'coming_soon');
  const comingSoonChallenges = challenges.filter(c => c.status === 'coming_soon');

  return (
    <div className="min-h-screen bg-[#09090b] text-white py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold" style={{fontFamily: 'Syne, sans-serif'}} data-testid="admin-challenges-title">
                Gestión de Challenges
              </h1>
              <p className="text-zinc-500 text-sm mt-1">Crea y administra tus challenges con materiales y comunidad</p>
            </div>
          </div>
          
          <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6" data-testid="create-challenge-button">
                <Plus className="w-5 h-5 mr-2" />
                Crear Challenge
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="text-white text-xl">{editingChallenge ? 'Editar Challenge' : 'Crear Nuevo Challenge'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                {/* Título */}
                <div>
                  <Label className="text-zinc-300">Título</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    className="bg-zinc-800 border-zinc-700 text-white focus:border-pink-500"
                    data-testid="challenge-title-input"
                  />
                </div>

                {/* Descripción */}
                <div>
                  <Label className="text-zinc-300">Descripción</Label>
                  <textarea
                    className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white focus:border-pink-500 focus:outline-none"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    data-testid="challenge-description-input"
                  />
                </div>

                {/* Status */}
                <div>
                  <Label className="text-zinc-300">Estado del Challenge</Label>
                  <div className="flex gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, status: 'active'})}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                        formData.status === 'active' 
                          ? 'border-green-500 bg-green-500/20 text-green-400' 
                          : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                      }`}
                    >
                      <Eye className="w-4 h-4" />
                      Activo
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, status: 'coming_soon'})}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                        formData.status === 'coming_soon' 
                          ? 'border-yellow-500 bg-yellow-500/20 text-yellow-400' 
                          : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                      }`}
                    >
                      <Clock className="w-4 h-4" />
                      Próximamente
                    </button>
                  </div>
                </div>

                {/* Tipo de Acceso */}
                <div className="border border-zinc-700 rounded-xl p-4">
                  <Label className="text-zinc-300 flex items-center gap-2 mb-3">
                    <Unlock className="w-4 h-4 text-green-400" />
                    Tipo de Acceso al Challenge
                  </Label>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, access_type: 'free', access_levels: []})}
                      className={`flex flex-col items-center justify-center gap-1 px-3 py-3 rounded-xl border-2 transition-all ${
                        formData.access_type === 'free' 
                          ? 'border-green-500 bg-green-500/20 text-green-400' 
                          : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                      }`}
                    >
                      <Unlock className="w-5 h-5" />
                      <span className="text-xs font-medium">Gratuito</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, access_type: 'paid', access_levels: []})}
                      className={`flex flex-col items-center justify-center gap-1 px-3 py-3 rounded-xl border-2 transition-all ${
                        formData.access_type === 'paid' 
                          ? 'border-pink-500 bg-pink-500/20 text-pink-400' 
                          : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                      }`}
                    >
                      <DollarSign className="w-5 h-5" />
                      <span className="text-xs font-medium">Solo Pago</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, access_type: 'levels'})}
                      className={`flex flex-col items-center justify-center gap-1 px-3 py-3 rounded-xl border-2 transition-all ${
                        formData.access_type === 'levels' 
                          ? 'border-purple-500 bg-purple-500/20 text-purple-400' 
                          : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                      }`}
                    >
                      <Crown className="w-5 h-5" />
                      <span className="text-xs font-medium">Por Nivel</span>
                    </button>
                  </div>
                  
                  {/* Descripción del tipo de acceso */}
                  <div className="text-xs text-zinc-500 mb-3">
                    {formData.access_type === 'free' && '✓ Todos los usuarios pueden inscribirse gratis'}
                    {formData.access_type === 'paid' && '✓ Todos los usuarios deben pagar el precio indicado'}
                    {formData.access_type === 'levels' && '✓ Los niveles seleccionados se inscriben gratis, el resto paga'}
                  </div>
                  
                  {/* Selector de niveles (solo si access_type es 'levels') */}
                  {formData.access_type === 'levels' && (
                    <div className="bg-zinc-800/50 rounded-xl p-3">
                      <p className="text-xs text-zinc-400 mb-3">Selecciona los niveles que tendrán acceso GRATIS:</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => toggleAccessLevel('nivel_1')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                            formData.access_levels?.includes('nivel_1')
                              ? 'border-pink-500 bg-pink-500/20 text-pink-400'
                              : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                          }`}
                        >
                          {formData.access_levels?.includes('nivel_1') && <Check className="w-3 h-3" />}
                          <span className="text-sm">Nivel 1</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAccessLevel('nivel_2')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                            formData.access_levels?.includes('nivel_2')
                              ? 'border-purple-500 bg-purple-500/20 text-purple-400'
                              : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                          }`}
                        >
                          {formData.access_levels?.includes('nivel_2') && <Check className="w-3 h-3" />}
                          <span className="text-sm">Nivel 2</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAccessLevel('premium')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                            formData.access_levels?.includes('premium')
                              ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                              : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                          }`}
                        >
                          {formData.access_levels?.includes('premium') && <Check className="w-3 h-3" />}
                          <Crown className="w-3 h-3" />
                          <span className="text-sm">Premium</span>
                        </button>
                      </div>
                      {formData.access_levels?.length > 0 && (
                        <p className="text-xs text-green-400 mt-2">
                          ✓ {formData.access_levels.join(', ')} tendrán acceso gratis
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Precio y Cupos */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Precio ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Cupos Máximos</Label>
                    <Input
                      type="number"
                      value={formData.max_participants}
                      onChange={(e) => setFormData({...formData, max_participants: e.target.value})}
                      placeholder="Ilimitado"
                      className="bg-zinc-800 border-zinc-700 text-white focus:border-pink-500"
                    />
                  </div>
                </div>

                {/* Fechas */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Fecha de Inicio</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Fecha de Fin</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white focus:border-pink-500"
                    />
                  </div>
                </div>

                {/* Tipo de Comunidad y Links */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-zinc-300 mb-2 block">Tipo de Comunidad</Label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, community_type: 'telegram'})}
                        className={`flex-1 px-4 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                          formData.community_type === 'telegram'
                            ? 'bg-blue-500 text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        <Send className="w-4 h-4" />
                        Telegram
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, community_type: 'whatsapp'})}
                        className={`flex-1 px-4 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                          formData.community_type === 'whatsapp'
                            ? 'bg-green-500 text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        <LinkIcon className="w-4 h-4" />
                        WhatsApp
                      </button>
                    </div>
                  </div>

                  {/* Link según el tipo seleccionado */}
                  {formData.community_type === 'telegram' ? (
                    <div>
                      <Label className="text-zinc-300 flex items-center gap-2">
                        <Send className="w-4 h-4 text-blue-400" />
                        Link de Telegram
                      </Label>
                      <Input
                        value={formData.telegram_link}
                        onChange={(e) => setFormData({...formData, telegram_link: e.target.value})}
                        placeholder="https://t.me/tu_grupo"
                        className="bg-zinc-800 border-zinc-700 text-white focus:border-pink-500"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-zinc-300 flex items-center gap-2">
                        <LinkIcon className="w-4 h-4 text-green-400" />
                        Link de WhatsApp
                      </Label>
                      <Input
                        value={formData.whatsapp_link}
                        onChange={(e) => setFormData({...formData, whatsapp_link: e.target.value})}
                        placeholder="https://chat.whatsapp.com/tu_grupo"
                        className="bg-zinc-800 border-zinc-700 text-white focus:border-pink-500"
                      />
                    </div>
                  )}
                  <p className="text-xs text-zinc-500">
                    Los usuarios inscritos verán el botón para unirse a este grupo
                  </p>
                </div>

                {/* Descuento */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Código de Descuento</Label>
                    <Input
                      value={formData.discount_code}
                      onChange={(e) => setFormData({...formData, discount_code: e.target.value.toUpperCase()})}
                      placeholder="Ej: VERANO2025"
                      className="bg-zinc-800 border-zinc-700 text-white focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Descuento (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.discount_percentage}
                      onChange={(e) => setFormData({...formData, discount_percentage: e.target.value})}
                      placeholder="0-100"
                      className="bg-zinc-800 border-zinc-700 text-white focus:border-pink-500"
                    />
                  </div>
                </div>

                {/* Plan Nutricional */}
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-green-400 flex items-center gap-2 text-base font-semibold">
                        🥗 Plan Nutricional
                      </Label>
                      <p className="text-xs text-zinc-400 mt-1">
                        Permite a los participantes crear su menú personalizado
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, has_nutrition_plan: !formData.has_nutrition_plan})}
                      className={`px-4 py-2 rounded-xl font-medium transition-all ${
                        formData.has_nutrition_plan
                          ? 'bg-green-500 text-white'
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {formData.has_nutrition_plan ? 'Habilitado' : 'Deshabilitado'}
                    </button>
                  </div>

                  {formData.has_nutrition_plan && (
                    <div>
                      <Label className="text-zinc-300 mb-2 block">Objetivo Nutricional del Challenge</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'perder', label: '🔥 Pérdida de Grasa', color: 'orange' },
                          { value: 'ganar', label: '💪 Aumento de Masa', color: 'blue' },
                          { value: 'mantener', label: '⚖️ Mantener Peso', color: 'purple' }
                        ].map(goal => (
                          <button
                            key={goal.value}
                            type="button"
                            onClick={() => setFormData({...formData, nutrition_goal: goal.value})}
                            className={`px-3 py-3 rounded-xl font-medium text-sm transition-all ${
                              formData.nutrition_goal === goal.value
                                ? goal.color === 'orange' ? 'bg-orange-500 text-white'
                                : goal.color === 'blue' ? 'bg-blue-500 text-white'
                                : 'bg-purple-500 text-white'
                                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                            }`}
                          >
                            {goal.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-zinc-500 mt-2">
                        Este objetivo se usará para calcular las calorías y macros de todos los participantes
                      </p>
                    </div>
                  )}
                </div>

                {/* Imagen */}
                <div>
                  <Label className="text-zinc-300">🖼️ Imagen del Challenge</Label>
                  {formData.image_url && !imageFile && (
                    <div className="mb-3 flex items-center gap-3 bg-zinc-800 rounded-xl p-3">
                      <img 
                        src={formData.image_url.startsWith('http') ? formData.image_url : `${BACKEND_URL}${formData.image_url}`}
                        alt="Imagen actual"
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <span className="text-xs text-zinc-400">Imagen actual</span>
                    </div>
                  )}
                  <div 
                    onClick={() => imageInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                      imageFile ? 'border-green-500 bg-green-500/10' : 'border-zinc-700 hover:border-pink-500 hover:bg-pink-500/5'
                    }`}
                  >
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setImageFile(file);
                      }}
                      className="hidden"
                    />
                    {imageFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <Check className="w-6 h-6 text-green-500" />
                        <div className="text-left">
                          <p className="font-medium text-green-400 text-sm">{imageFile.name}</p>
                          <p className="text-xs text-green-500">{formatFileSize(imageFile.size)}</p>
                        </div>
                        <button 
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setImageFile(null); }}
                          className="ml-2 text-red-400 hover:text-red-300"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <>
                        <Image className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                        <p className="text-sm text-zinc-400">
                          {formData.image_url ? 'Cambiar imagen' : 'Subir imagen'}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Materiales Descargables */}
                <div className="border border-zinc-700 rounded-xl p-4">
                  <Label className="text-zinc-300 flex items-center gap-2 mb-4">
                    <FileText className="w-4 h-4 text-purple-400" />
                    Materiales Descargables (PDFs, Videos, etc.)
                  </Label>
                  
                  {/* Lista de materiales actuales */}
                  {formData.materials.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {formData.materials.map((material, index) => (
                        <div key={index} className="flex items-center justify-between bg-zinc-800 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            {material.type === 'pdf' && <FileText className="w-5 h-5 text-red-400" />}
                            {material.type === 'video' && <Video className="w-5 h-5 text-blue-400" />}
                            {material.type === 'file' && <Download className="w-5 h-5 text-green-400" />}
                            <div>
                              <p className="text-sm font-medium text-white">{material.name}</p>
                              <p className="text-xs text-zinc-500">{material.type.toUpperCase()}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeMaterial(index)}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Subir nuevo material */}
                  <div className="space-y-3">
                    <Input
                      placeholder="Nombre del material (ej: Guía de Nutrición)"
                      value={materialName}
                      onChange={(e) => setMaterialName(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                    
                    {/* Opción 1: Pegar enlace directo (Google Drive, Dropbox, etc.) */}
                    <div className="bg-zinc-800/50 rounded-xl p-3 border border-zinc-700">
                      <p className="text-xs text-zinc-400 mb-2">📎 Opción 1: Pegar enlace directo (Google Drive, Dropbox, etc.)</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="https://drive.google.com/file/d/..."
                          value={materialUrl}
                          onChange={(e) => setMaterialUrl(e.target.value)}
                          className="bg-zinc-900 border-zinc-600 text-white text-sm"
                        />
                        <Button
                          type="button"
                          onClick={handleAddMaterialByUrl}
                          disabled={!materialUrl || !materialName.trim()}
                          className="bg-green-600 hover:bg-green-700 text-white px-4"
                        >
                          <LinkIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Opción 2: Subir archivo (hasta 10MB) */}
                    <div className="bg-zinc-800/50 rounded-xl p-3 border border-zinc-700">
                      <p className="text-xs text-zinc-400 mb-2">📤 Opción 2: Subir archivo (hasta 10MB)</p>
                      <div className="flex gap-2">
                        <div 
                          onClick={() => materialInputRef.current?.click()}
                          className={`flex-1 border-2 border-dashed rounded-xl p-3 text-center cursor-pointer transition-colors ${
                            materialFile ? 'border-green-500 bg-green-500/10' : 'border-zinc-700 hover:border-purple-500'
                          }`}
                        >
                          <input
                            ref={materialInputRef}
                            type="file"
                            accept=".pdf,.mp4,.mov,.doc,.docx,.xls,.xlsx"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) setMaterialFile(file);
                            }}
                            className="hidden"
                          />
                          {materialFile ? (
                            <p className="text-sm text-green-400">{materialFile.name}</p>
                          ) : (
                            <p className="text-sm text-zinc-400">Seleccionar archivo</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          onClick={handleUploadMaterial}
                          disabled={uploadingMaterial || !materialFile || !materialName.trim()}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-4"
                        >
                          {uploadingMaterial ? 'Subiendo...' : (
                            <span className="flex items-center gap-2">
                              <Upload className="w-4 h-4" />
                            </span>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setIsOpen(false); resetForm(); }}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-pink-600 hover:bg-pink-700 text-white" 
                    data-testid="submit-challenge-button"
                    disabled={uploading}
                  >
                    {uploading ? 'Guardando...' : (editingChallenge ? 'Actualizar' : 'Crear Challenge')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
            <p className="text-zinc-500 text-sm">Total Challenges</p>
            <p className="text-2xl font-bold text-white">{challenges.length}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
            <p className="text-zinc-500 text-sm">Activos</p>
            <p className="text-2xl font-bold text-green-400">{activeChallenges.length}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
            <p className="text-zinc-500 text-sm">Próximamente</p>
            <p className="text-2xl font-bold text-yellow-400">{comingSoonChallenges.length}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
            <p className="text-zinc-500 text-sm">Total Inscritos</p>
            <p className="text-2xl font-bold text-pink-400">
              {Object.values(registrations).reduce((acc, regs) => acc + (regs?.length || 0), 0)}
            </p>
          </div>
        </div>

        {/* Challenges Activos */}
        {activeChallenges.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-green-400" />
              Challenges Activos
            </h2>
            <div className="space-y-4">
              {activeChallenges.map((challenge) => (
                <ChallengeAdminCard 
                  key={challenge.challenge_id} 
                  challenge={challenge} 
                  onEdit={handleEdit} 
                  onDelete={handleDelete}
                  registrations={registrations[challenge.challenge_id] || []}
                  expanded={expandedChallenge === challenge.challenge_id}
                  onToggleExpand={() => setExpandedChallenge(
                    expandedChallenge === challenge.challenge_id ? null : challenge.challenge_id
                  )}
                  onExportCSV={exportRegistrationsToCSV}
                  onCreateTestAccount={() => setShowTestAccountModal(challenge.challenge_id)}
                  onDeleteRegistration={handleDeleteRegistration}
                />
              ))}
            </div>
          </div>
        )}

        {/* Challenges Próximamente */}
        {comingSoonChallenges.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              Próximamente
            </h2>
            <div className="space-y-4">
              {comingSoonChallenges.map((challenge) => (
                <ChallengeAdminCard 
                  key={challenge.challenge_id} 
                  challenge={challenge} 
                  onEdit={handleEdit} 
                  onDelete={handleDelete}
                  registrations={[]}
                  expanded={false}
                  onToggleExpand={() => {}}
                  onExportCSV={() => {}}
                  onCreateTestAccount={() => setShowTestAccountModal(challenge.challenge_id)}
                  onDeleteRegistration={handleDeleteRegistration}
                  isComingSoon
                />
              ))}
            </div>
          </div>
        )}

        {/* Sin challenges */}
        {challenges.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-10 h-10 text-zinc-600" />
            </div>
            <h3 className="text-xl font-bold text-zinc-400 mb-2">No hay challenges</h3>
            <p className="text-zinc-500 mb-4">Crea tu primer challenge para comenzar</p>
            <Button onClick={() => setIsOpen(true)} className="bg-pink-600 hover:bg-pink-700 rounded-full px-6">
              <Plus className="w-4 h-4 mr-2" />
              Crear Challenge
            </Button>
          </div>
        )}
        
        {/* Modal para crear cuenta de prueba */}
        <Dialog open={!!showTestAccountModal} onOpenChange={(open) => !open && setShowTestAccountModal(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                Crear Cuenta de Prueba
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-zinc-400 text-sm">
                Crea una cuenta para inscribir manualmente a un usuario en este challenge.
              </p>
              <div>
                <Label className="text-zinc-400">Nombre</Label>
                <Input
                  value={testAccountData.name}
                  onChange={(e) => setTestAccountData({...testAccountData, name: e.target.value})}
                  placeholder="Nombre del usuario"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Email *</Label>
                <Input
                  type="email"
                  value={testAccountData.email}
                  onChange={(e) => setTestAccountData({...testAccountData, email: e.target.value})}
                  placeholder="email@ejemplo.com"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400">Contraseña *</Label>
                <Input
                  type="text"
                  value={testAccountData.password}
                  onChange={(e) => setTestAccountData({...testAccountData, password: e.target.value})}
                  placeholder="Mínimo 6 caracteres"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  required
                />
                <p className="text-zinc-500 text-xs mt-1">El usuario usará esta contraseña para iniciar sesión</p>
              </div>
              <Button 
                onClick={() => handleCreateTestAccount(showTestAccountModal)}
                disabled={creatingTestAccount}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {creatingTestAccount ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Cuenta y Agregar al Challenge
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Componente de tarjeta de challenge para admin (expandible)
const ChallengeAdminCard = ({ challenge, onEdit, onDelete, registrations, expanded, onToggleExpand, onExportCSV, onCreateTestAccount, onDeleteRegistration, isComingSoon }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  return (
    <div 
      className={`bg-zinc-900 border rounded-2xl overflow-hidden transition-all ${
        isComingSoon ? 'border-yellow-500/30' : 'border-zinc-800 hover:border-pink-500/30'
      }`}
    >
      {/* Cabecera del challenge */}
      <div className="flex items-center gap-4 p-4">
        {/* Imagen miniatura */}
        <div className="w-20 h-20 rounded-xl overflow-hidden bg-zinc-800 flex-shrink-0">
          {challenge.image_url ? (
            <img 
              src={challenge.image_url.startsWith('http') ? challenge.image_url : `${BACKEND_URL}${challenge.image_url}`} 
              alt={challenge.title} 
              className={`w-full h-full object-cover ${isComingSoon ? 'opacity-60' : ''}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Calendar className="w-8 h-8 text-zinc-600" />
            </div>
          )}
        </div>
        
        {/* Info principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-white truncate">{challenge.title}</h3>
            {isComingSoon && (
              <span className="bg-yellow-500 text-black text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                Próximamente
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="text-pink-400 font-bold">${challenge.price || 0}</span>
            {challenge.start_date && (
              <span className="text-zinc-500 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(challenge.start_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
              </span>
            )}
            <span className="text-zinc-500 flex items-center gap-1">
              <Users className="w-3 h-3" />
              {registrations.length} inscritos
            </span>
            {challenge.telegram_link && (
              <a 
                href={challenge.telegram_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 flex items-center gap-1 hover:text-blue-300"
              >
                <Send className="w-3 h-3" />
                Telegram
              </a>
            )}
            {challenge.materials?.length > 0 && (
              <span className="text-purple-400 flex items-center gap-1">
                <FileText className="w-3 h-3" />
                {challenge.materials.length} materiales
              </span>
            )}
          </div>
        </div>
        
        {/* Acciones */}
        <div className="flex items-center gap-2">
          {!isComingSoon && (
            <Button
              onClick={onToggleExpand}
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white"
              title={registrations.length > 0 ? `Ver ${registrations.length} inscritos` : 'Gestionar inscritos'}
            >
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </Button>
          )}
          <Button
            onClick={() => onEdit(challenge)}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => onDelete(challenge.challenge_id)}
            variant="outline"
            size="sm"
            className="border-red-900/50 text-red-400 hover:bg-red-900/20"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Panel expandido con inscritos */}
      {expanded && (
        <div className="border-t border-zinc-800 p-4 bg-zinc-950">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-bold text-white flex items-center gap-2">
              <Users className="w-4 h-4 text-pink-500" />
              Usuarios Inscritos ({registrations.length})
            </h4>
            <div className="flex gap-2">
              <Button
                onClick={onCreateTestAccount}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Crear Cuenta Prueba
              </Button>
              {registrations.length > 0 && (
                <Button
                  onClick={() => onExportCSV(challenge.challenge_id, challenge.title)}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Exportar CSV
                </Button>
              )}
            </div>
          </div>
          
          {registrations.length > 0 ? (
            <div className="bg-zinc-900 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-zinc-800">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-zinc-400">Nombre</th>
                    <th className="text-left p-3 text-sm font-medium text-zinc-400">Email</th>
                    <th className="text-left p-3 text-sm font-medium text-zinc-400">WhatsApp</th>
                    <th className="text-left p-3 text-sm font-medium text-zinc-400">Fecha</th>
                    <th className="text-center p-3 text-sm font-medium text-zinc-400">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {registrations.map((reg, idx) => (
                  <tr key={idx} className="hover:bg-zinc-800/50">
                    <td className="p-3 text-sm text-white">{reg.name || 'N/A'}</td>
                    <td className="p-3 text-sm text-zinc-400">{reg.email || 'N/A'}</td>
                    <td className="p-3 text-sm">
                      {reg.whatsapp ? (
                        <a 
                          href={`https://wa.me/${reg.whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-400 hover:text-green-300"
                        >
                          {reg.whatsapp}
                        </a>
                      ) : 'N/A'}
                    </td>
                    <td className="p-3 text-sm text-zinc-500">
                      {reg.registered_at ? new Date(reg.registered_at).toLocaleDateString('es-ES') : 'N/A'}
                    </td>
                    <td className="p-3 text-sm text-center">
                      <Button
                        onClick={() => onDeleteRegistration(challenge.challenge_id, reg._id || reg.user_id, reg.email)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        title="Eliminar inscripción"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ) : (
            <div className="bg-zinc-900/50 border border-dashed border-zinc-800 rounded-xl p-6 text-center">
              <Users className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-500">No hay usuarios inscritos aún</p>
              <p className="text-zinc-600 text-sm mt-1">Usa "Crear Cuenta Prueba" para agregar usuarios manualmente</p>
            </div>
          )}
          
          {/* Materiales */}
          {challenge.materials?.length > 0 && (
            <div className="mt-4">
              <h4 className="font-bold text-white flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-purple-500" />
                Materiales del Challenge
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {challenge.materials.map((material, idx) => (
                  <a
                    key={idx}
                    href={material.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3 hover:bg-zinc-700 transition-colors"
                  >
                    {material.type === 'pdf' && <FileText className="w-4 h-4 text-red-400" />}
                    {material.type === 'video' && <Video className="w-4 h-4 text-blue-400" />}
                    {material.type === 'file' && <Download className="w-4 h-4 text-green-400" />}
                    <span className="text-sm text-white truncate">{material.name}</span>
                    <ExternalLink className="w-3 h-3 text-zinc-500 ml-auto" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminChallenges;
